var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback } from 'react';
import { Menu } from '@headlessui/react';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon.js';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Icon } from '../Icon/index.js';
import { ChevronDownIcon } from '../icons/index.js';
import { FloatingContextProvider, FloatingWrapper, useFloatingContext, } from '../utils/floating/index.js';
import { runIfFn } from '../utils/index.js';
import { DropdownProvider, useDropdown } from './DropdownContext.js';
export const Dropdown = (props) => {
    const { children, as, className, placement = 'bottom-start' } = props;
    return (React.createElement(Menu, { as: as || 'div', className: twMerge(clsx('relative inline-block text-left', className)) }, renderProps => (React.createElement(FloatingContextProvider, { placement: placement },
        React.createElement(DropdownProvider, { open: renderProps.open }, runIfFn(children, renderProps))))));
};
const Button = props => {
    const { children, icon, className } = props, rest = __rest(props, ["children", "icon", "className"]);
    const { reference } = useFloatingContext();
    const { backgroundType } = useBackgroundContext();
    const buttonClsx = [
        backgroundType === 'main' &&
            'text-basicSurface-700 bg-surface-50 hover:bg-surface-100',
        backgroundType === 'surface' &&
            'text-basicSurface-700 bg-surface-50 hover:bg-surface-100',
        backgroundType === 'secondary' &&
            'text-basicSecondary-700 bg-actionSecondary-50 hover:bg-actionSecondary-100',
    ];
    return (React.createElement(Menu.Button, Object.assign({ ref: reference, className: twMerge(clsx('inline-flex justify-center items-center w-full rounded-md border border-basicSurface-300/25 shadow-sm px-4 py-2 text-sm font-medium ', buttonClsx, 'focus:outline-none focus-visible:ring', className)) }, rest),
        React.createElement("span", { className: "sr-only" }, "Open options"),
        children,
        React.createElement(Icon, { className: "-mr-1 ml-2 h-5 w-5" }, icon || React.createElement(ChevronDownIcon, null))));
};
const ButtonMinimal = props => {
    const { className, children } = props, rest = __rest(props, ["className", "children"]);
    const { reference } = useFloatingContext();
    const { backgroundType } = useBackgroundContext();
    const buttonClsx = [
        backgroundType === 'main' && 'text-basicMain-400 hover:text-basicMain-600',
        backgroundType === 'surface' &&
            ' text-basicSurface-400 hover:text-basicSurface-600',
        backgroundType === 'secondary' &&
            ' text-basicSecondary-400 hover:text-basicSecondary-600',
    ];
    return (React.createElement(Menu.Button, Object.assign({ ref: reference, className: twMerge(clsx('rounded-full flex items-center', buttonClsx, 'focus:outline-none focus-visible:ring', className)) }, rest), children || (React.createElement(React.Fragment, null,
        React.createElement("span", { className: "sr-only" }, "Open options"),
        React.createElement(DotsHorizontalIcon, { className: "h-5 w-5", "aria-hidden": "true" })))));
};
const Items = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { open } = useDropdown();
    const { triggerRef } = useFloatingContext();
    const close = useCallback(() => {
        var _a;
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, [triggerRef]);
    return (React.createElement(FloatingWrapper, { open: open },
        React.createElement(Menu.Items, Object.assign({ static: true, className: clsx('py-1 rounded-md shadow-lg bg-surface-50 border border-surface-200', 'focus-visible:ring focus:outline-none', className) }, rest), runIfFn(children, { close }))));
};
const Item = props => {
    const { as, children, leadingIcon, disabled, className } = props, rest = __rest(props, ["as", "children", "leadingIcon", "disabled", "className"]);
    const Component = as || 'a';
    return (React.createElement(Menu.Item, { disabled: disabled }, ({ active }) => (React.createElement(Component, Object.assign({ className: clsx(active
            ? 'bg-surface-100 text-basicSurface-900'
            : 'bg-surface-50 text-basicSurface-700', 'flex items-center px-4 py-2', disabled
            ? 'opacity-60 cursor-default pointer-events-none'
            : 'cursor-pointer', className) }, rest),
        leadingIcon && (React.createElement(Icon, { className: "mr-3 h-5 w-5 text-basicSurface-400 group-hover:text-basicSurface-500" }, leadingIcon)),
        children))));
};
Dropdown.Button = Button;
Dropdown.ButtonMinimal = ButtonMinimal;
Dropdown.Items = Items;
Dropdown.Item = Item;
//# sourceMappingURL=Dropdown.js.map